// VozidlaTerminy — zoznam všetkých áut a ich termínov na jednej strane (Mirko 18. 9. 2026:
// „vždy chcem zoznam vozidiel; ak kliknem na STK a diaľničnú, stĺpce s koncom platnosti, nie odkaz na vozidlá;
//  vyrobíš stránku, kde je listový zoznam áut a tam všetko vidíš; zvýrazníš, ktoré skončili alebo v blízkej dobe skončia;
//  Esc zrušiť, Ctrl+P vytlačiť alebo e-mail“).
// Stĺpce: ŠPZ, vozidlo, STK, emisná, diaľničná známka, PZP, havarijná. Po termíne červené, do 30 dní oranžové.
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchAuth } from '../auth/fetchAuth.js'
import { PageHeader, SearchInput, FilterChips, ListStatusBar } from '../components/Ui.jsx'
import { BtnGhost, BtnNav } from '../components/Btn.jsx'
import { useToast } from '../components/Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS, SK, hladaj } from '../theme.js'

const DNI_BLIZKO = 30          // „v blízkej dobe“ = do 30 dní
const dnesStr = () => new Date().toISOString().slice(0, 10)
const dni = (d) => (!d ? null : Math.round((new Date(String(d).slice(0, 10)) - new Date(dnesStr())) / 86400000))
const POLIA = [
  { kluc: 'stk', label: 'STK' },
  { kluc: 'ek', label: 'Emisná' },
  { kluc: 'vignette', label: 'Diaľničná' },
  { kluc: 'insurance', label: 'PZP' },
  { kluc: 'green_card', label: 'Zelená karta' },
  { kluc: 'havarijna_do', label: 'Havarijná' },
]

function Datum({ hodnota }) {
  const d = String(hodnota || '').slice(0, 10)
  if (!d) return <span style={{ color: COLORS.disabled }}>—</span>
  const n = dni(d)
  const farba = n < 0 ? COLORS.remove : n <= DNI_BLIZKO ? COLORS.warn : COLORS.text
  const pozadie = n < 0 ? 'rgba(220,38,38,0.10)' : n <= DNI_BLIZKO ? 'rgba(217,119,6,0.12)' : 'transparent'
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, padding: '2px 7px', borderRadius: RADIUS.sm, background: pozadie }}>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: n <= DNI_BLIZKO ? TYPO.weights.bold : 400, color: farba }}>{d.split('-').reverse().join('.')}</span>
      <span style={{ fontSize: 10, color: farba }}>{n < 0 ? 'po termíne ' + Math.abs(n) + ' dní' : n === 0 ? 'dnes' : 'o ' + n + ' dní'}</span>
    </span>
  )
}

export default function VozidlaTerminy() {
  const navigate = useNavigate()
  const toast = useToast()
  const [vozidla, setVozidla] = useState([])
  const [hladat, setHladat] = useState('')
  const [filter, setFilter] = useState('pozor')
  const [nacitava, setNacitava] = useState(true)
  const [posiela, setPosiela] = useState(false)

  useEffect(() => {
    fetchAuth('/api/vehicles').then(r => r.ok ? r.json() : []).then(d => setVozidla((Array.isArray(d) ? d : (d && d.rows) || []).filter(v => v && v.active !== 0)))
      .catch(() => toast.error('Vozidlá sa nenačítali')).finally(() => setNacitava(false))
  }, [])

  const spocitane = useMemo(() => vozidla.map(v => {
    const terminy = POLIA.map(p => ({ ...p, datum: String(v[p.kluc] || '').slice(0, 10), dni: dni(v[p.kluc]) })).filter(x => x.datum)
    const najhorsi = terminy.reduce((a, x) => (a === null || x.dni < a ? x.dni : a), null)
    return { ...v, terminy, najhorsi, poTermine: terminy.some(x => x.dni < 0), blizko: terminy.some(x => x.dni >= 0 && x.dni <= DNI_BLIZKO) }
  }).sort((a, b) => (a.najhorsi === null ? 1 : b.najhorsi === null ? -1 : a.najhorsi - b.najhorsi)), [vozidla])

  const pocty = useMemo(() => ({
    vsetky: spocitane.length,
    pozor: spocitane.filter(v => v.poTermine || v.blizko).length,
    poTermine: spocitane.filter(v => v.poTermine).length,
    chyba: spocitane.filter(v => v.terminy.length < 3).length,
  }), [spocitane])

  const zoznam = useMemo(() => {
    const q = hladat.trim().toLowerCase()
    return spocitane.filter(v => {
      if (filter === 'pozor' && !(v.poTermine || v.blizko)) return false
      if (filter === 'po' && !v.poTermine) return false
      if (filter === 'chyba' && v.terminy.length >= 3) return false
      if (!q) return true
      return hladaj([v.plate, v.name, v.brand, v.model, v.vin].filter(Boolean).join(' '), q)
    })
  }, [spocitane, filter, hladat])

  const zavri = useCallback(() => navigate(-1), [navigate])
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape' && !document.querySelector('[aria-modal="true"]')) { e.preventDefault(); zavri() }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) { /* tlač rieši prehliadač, len nech nezmizne stránka */ }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [zavri])

  const [okno, setOkno] = useState(null)   // { komu, predmet }
  const otvorOdoslat = () => setOkno({
    komu: '', predmet: 'Termíny vozidiel KAMIR — ' + new Date().toLocaleDateString('sk-SK'),
    text: 'Dobrý deň,\n\nv prílohe posielam prehľad termínov našich vozidiel (STK, emisná, diaľničná známka, PZP). Červené sú po termíne, oranžové končia do ' + DNI_BLIZKO + ' dní.\n\nS pozdravom\nKAMIR s.r.o.',
  })
  const posliEmailom = async () => {
    if (!okno || !okno.komu.trim()) { toast.error('Zadaj e-mail príjemcu'); return }
    setPosiela(true)
    try {
      const r = await fetchAuth('/api/vozidla-terminy/odoslat', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ komu: okno.komu, predmet: okno.predmet, text: okno.text, dni: DNI_BLIZKO }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || d.ok === false) { toast.error('Odoslanie zlyhalo: ' + (d.error || 'HTTP ' + r.status)); return }
      toast.success('Odoslané na ' + (d.komu || okno.komu))
      setOkno(null)
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setPosiela(false) }
  }

  const th = { position: 'sticky', top: 0, zIndex: 1, background: '#eef5fa', color: COLORS.nav, fontSize: 11, fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.03em', padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #d6e6f2', whiteSpace: 'nowrap' }
  const td = { padding: '7px 10px', borderBottom: `1px solid ${COLORS.lineSoft}`, fontSize: TYPO.sizes.sm, verticalAlign: 'middle', whiteSpace: 'nowrap' }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: SPACE.sm, minHeight: 0 }}>
      <style>{`@media print {
        body * { visibility: hidden; }
        #vozidla-tlac, #vozidla-tlac * { visibility: visible; }
        #vozidla-tlac { position: absolute; left: 0; top: 0; width: 100%; }
        #vozidla-tlac .bez-tlace { display: none !important; }
      }`}</style>
      <PageHeader title="Vozidlá — termíny" subtitle="STK, emisná, diaľničná známka, PZP a havarijná na jednom mieste. Po termíne je červené, do 30 dní oranžové. Esc zavrie, Ctrl+P vytlačí.">
        <BtnGhost onClick={() => window.print()}>Tlačiť (Ctrl+P)</BtnGhost>
        <BtnGhost onClick={otvorOdoslat}>Poslať e-mailom</BtnGhost>
        <BtnNav onClick={() => navigate('/vozidla')}>Karty vozidiel</BtnNav>
        <BtnGhost onClick={zavri}>Zavrieť (Esc)</BtnGhost>
      </PageHeader>

      <div className="bez-tlace" style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 240, flex: '0 1 320px' }}>
          <SearchInput value={hladat} onChange={v => setHladat(typeof v === 'string' ? v : (v && v.target ? v.target.value : ''))} placeholder="Hľadať ŠPZ, značku alebo model…" />
        </div>
        <FilterChips value={filter} onChange={setFilter} options={[
          { key: 'pozor', label: 'Vyžaduje pozornosť · ' + pocty.pozor, color: COLORS.warn },
          { key: 'po', label: 'Po termíne · ' + pocty.poTermine, color: COLORS.remove },
          { key: 'chyba', label: 'Chýbajú termíny · ' + pocty.chyba },
          { key: 'vsetky', label: 'Všetky autá · ' + pocty.vsetky },
        ]} />
      </div>

      <div id="vozidla-tlac" style={{ flex: 1, minHeight: 240, overflow: 'auto', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.bg }}>
        <div style={{ display: 'none' }} className="len-tlac" />
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead><tr>
            <th style={th}>ŠPZ</th>
            <th style={th}>Vozidlo</th>
            {POLIA.map(p => <th key={p.kluc} style={th}>{p.label}</th>)}
            <th style={th}>Poznámka</th>
          </tr></thead>
          <tbody>
            {nacitava ? <tr><td colSpan={POLIA.length + 3} style={{ ...td, color: COLORS.muted, padding: SPACE.lg }}>Načítavam…</td></tr>
              : zoznam.length === 0 ? <tr><td colSpan={POLIA.length + 3} style={{ ...td, color: COLORS.muted, padding: SPACE.lg }}>Nič nezodpovedá filtru.</td></tr>
                : zoznam.map(v => (
                  <tr key={v.id} onClick={() => navigate('/vozidla')} style={{ cursor: 'pointer', background: v.poTermine ? 'rgba(220,38,38,0.05)' : 'transparent' }} title="Otvoriť karty vozidiel">
                    <td style={{ ...td, fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold }}>{v.plate || '—'}</td>
                    <td style={td}>{[v.brand, v.model].filter(Boolean).join(' ') || v.name || ''}</td>
                    {POLIA.map(p => <td key={p.kluc} style={td}><Datum hodnota={v[p.kluc]} /></td>)}
                    <td style={{ ...td, whiteSpace: 'normal', color: COLORS.muted, fontSize: TYPO.sizes.xs, maxWidth: 260 }}>{v.terminy.length < 3 ? 'chýbajú termíny — doplň v karte vozidla' : (v.notes || '')}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {okno && (
        <div role="dialog" aria-modal="true" aria-label="Poslať termíny vozidiel e-mailom" onClick={() => setOkno(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: COLORS.bg, borderRadius: RADIUS.lg, width: 'min(560px, 96vw)', padding: SPACE.lg, display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
            <b style={{ fontSize: TYPO.sizes.lg }}>Poslať termíny vozidiel e-mailom</b>
            <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Komu (viac adries oddeľ čiarkou)
              <input value={okno.komu} onChange={e => setOkno(o => ({ ...o, komu: e.target.value }))} placeholder="meno@firma.sk"
                style={{ width: '100%', marginTop: 4, padding: '9px 11px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box' }} />
            </label>
            <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Predmet
              <input value={okno.predmet} onChange={e => setOkno(o => ({ ...o, predmet: e.target.value }))}
                style={{ width: '100%', marginTop: 4, padding: '9px 11px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box' }} />
            </label>
            <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Správa
              <textarea value={okno.text} onChange={e => setOkno(o => ({ ...o, text: e.target.value }))} rows={5}
                style={{ width: '100%', marginTop: 4, padding: '9px 11px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box', resize: 'vertical' }} />
            </label>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Tabuľka s termínmi ide priamo v e-maile. Kópia ostane v odoslanej pošte firemnej schránky.</div>
            <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
              <BtnGhost onClick={() => setOkno(null)} disabled={posiela}>Zrušiť</BtnGhost>
              <BtnNav onClick={posliEmailom} disabled={posiela}>{posiela ? 'Posielam…' : 'Odoslať'}</BtnNav>
            </div>
          </div>
        </div>
      )}
      <ListStatusBar>{zoznam.length} {zoznam.length === 1 ? 'auto' : zoznam.length < 5 ? 'autá' : 'áut'} · po termíne {pocty.poTermine} · do {DNI_BLIZKO} dní {pocty.pozor - pocty.poTermine} · Esc zavrie · Ctrl+P vytlačí</ListStatusBar>
    </div>
  )
}
